import React, { useEffect, useRef, useState } from 'react'
import { telecomunication } from '../../translate'
import { Paper, Box, Typography, Alert, TextField, Autocomplete, Button, Grid, Popper } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons'
import Api from '../../Api'
import { toast } from 'react-toastify'
import { QRCodeSVG } from 'qrcode.react'

const mainNumberInputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#7ebc0a' },
    '&:hover fieldset': { borderColor: '#6aa309' },
    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
  },
}

const DEFAULT_BOT_URL = 'https://t.me/siptg'
const DEFAULT_BOT_USERNAME = '@siptg'

export default function TelegramInclude({ botUrl = DEFAULT_BOT_URL, botUsername = DEFAULT_BOT_USERNAME }) {
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [includedData, setIncludedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedBuddy, setSelectedBuddy] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [pass, setPass] = useState('')
  const [username, setUsername] = useState('')
  const [askUsername, setAskUsername] = useState(false)
  const [saving, setSaving] = useState(false)
  const [qrAnchorEl, setQrAnchorEl] = useState(null)
  const qrCloseTimeoutRef = useRef(null)

  const handleQrOpen = (e) => {
    if (qrCloseTimeoutRef.current) clearTimeout(qrCloseTimeoutRef.current)
    setQrAnchorEl(e.currentTarget)
  }
  const handleQrClose = () => {
    qrCloseTimeoutRef.current = setTimeout(() => setQrAnchorEl(null), 150)
  }
  const handleQrEnter = () => {
    if (qrCloseTimeoutRef.current) clearTimeout(qrCloseTimeoutRef.current)
  }
  const handleQrLeave = () => {
    qrCloseTimeoutRef.current = setTimeout(() => setQrAnchorEl(null), 150)
  }

  const fetchData = async () => {
    try {
      const res = await Api.telegramIncluded()
      setIncludedData(res.data?.data || [])
      setLoading(false)
    } catch (e) {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleSave = async () => {
    if (!selectedBuddy || !phoneNumber.trim()) {
      toast.error(telecomunication.telegram_select_error[langIndex])
      return
    }
    if (!pass) {
      toast.error(langIndex === 2 ? 'Не удалось получить секрет SIP. Обновите страницу.' : langIndex === 3 ? 'Չհաջողվեց ստանալ SIP գաղտնիքը։ Թարմացրեք էջը։' : 'Failed to load SIP secret. Please refresh.')
      return
    }

    setSaving(true)
    try {
      await Api.sipTelegramCreate({
        buddy: selectedBuddy,
        phone: phoneNumber.trim(),
        pass,
        ...(askUsername && username.trim() ? { username: username.trim() } : {})
      })
      toast.success(telecomunication.telegram_create_success[langIndex])
      setSelectedBuddy('')
      setPhoneNumber('')
      setPass('')
      setUsername('')
      setAskUsername(false)
      await fetchData()
    } catch (e) {
      const msg = e?.response?.data?.message
      // Some servers require telegram username after first attempt
      if (msg && String(msg).toLowerCase().includes('username')) {
        setAskUsername(true)
      } else {
        setAskUsername(true)
      }
      toast.error(msg || telecomunication.telegram_create_error[langIndex])
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (buddy) => {
    if (!window.confirm(telecomunication.telegram_delete_confirm[langIndex])) {
      return
    }

    try {
      await Api.sipTelegramDelete(buddy)
      toast.success(telecomunication.telegram_delete_success[langIndex])
      await fetchData()
    } catch (e) {
      toast.error(telecomunication.telegram_delete_error[langIndex])
      console.error(e)
    }
  }

  if (loading) return <Typography>Loading...</Typography>
  
  const created = includedData.filter(item => item.telegram)
  const toBeCreated = includedData.filter(item => !item.telegram)

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '1px solid #e5e8ed' }}>
      </Box>

      <Alert 
        icon={<InfoIcon />} 
        severity="success" 
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" component="span" sx={{ display: 'block' }}>
          {telecomunication.telegram_included_number_description[langIndex]}
        </Typography>
        <Typography variant="body2" component="span" sx={{ display: 'block', mt: 1 }}>
          {telecomunication.telegram_calls_from_bot_before[langIndex]}
          <Box
            component="span"
            onMouseEnter={handleQrOpen}
            onMouseLeave={handleQrClose}
            sx={{
              borderBottom: '1px dotted currentColor',
              cursor: 'help',
              fontWeight: 600,
            }}
          >
            {telecomunication.telegram_calls_from_bot_this[langIndex]}
          </Box>
          {telecomunication.telegram_calls_from_bot_after[langIndex]}
        </Typography>
        <Popper
          open={Boolean(qrAnchorEl)}
          anchorEl={qrAnchorEl}
          placement="top-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Box
            onMouseEnter={handleQrEnter}
            onMouseLeave={handleQrLeave}
            sx={{
              p: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: 6,
              width: 220,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <QRCodeSVG value={botUrl} size={160} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
              {botUsername}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              {botUrl}
            </Typography>
          </Box>
        </Popper>
      </Alert>

      <Box sx={{ mb: 4 }}>
        {toBeCreated.length > 0 ? (
          <Paper sx={{ p: 2, border: '1px solid #7ebc0a' }}>
            <Typography variant="h6" sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid #e5e8ed', fontWeight: 700 }}>
              {telecomunication.telegram_to_be_created[langIndex]}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={toBeCreated}
                  getOptionLabel={(option) => `${option.buddy}${option.notes ? ` (${option.notes})` : ''}`}
                  value={toBeCreated.find(item => item.buddy === selectedBuddy) || null}
                  onChange={async (event, newValue) => {
                    const buddy = newValue ? newValue.buddy : ''
                    setSelectedBuddy(buddy)
                    setPhoneNumber('')
                    setUsername('')
                    setAskUsername(false)
                    setPass('')
                    if (!buddy) return
                    try {
                      const res = await Api.sipSingle(buddy)
                      // sipSingle response shape varies across servers; try common locations
                      const secret =
                        res?.data?.single?.sip_buddy?.secret ||
                        res?.data?.single?.sip_secret ||
                        res?.data?.single?.secret ||
                        res?.data?.sip_buddy?.secret ||
                        res?.data?.dataItem?.secret ||
                        res?.data?.secret ||
                        res?.data?.data?.secret
                      if (secret) setPass(secret)
                    } catch (e) {
                      console.error(e)
                      setPass('')
                    }
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={telecomunication.telegram_select_number[langIndex]}
                      sx={mainNumberInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={telecomunication.telegram_phone_number[langIndex]}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={telecomunication.telegram_phone_placeholder[langIndex]}
                  sx={mainNumberInputSx}
                  disabled={!selectedBuddy}
                />
              </Grid>
              {askUsername && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="@username"
                    sx={mainNumberInputSx}
                    disabled={!selectedBuddy}
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !selectedBuddy || !phoneNumber.trim() || (askUsername && !username.trim())}
                  sx={{
                    backgroundColor: '#7ebc0a',
                    color: 'white',
                    '&:hover': { backgroundColor: '#6aa309' },
                    '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
                  }}
                >
                  {saving ? (langIndex === 1 ? 'Saving...' : langIndex === 2 ? 'Сохранение...' : 'Պահպանվում է...') : (telecomunication.telegram_save_button?.[langIndex] || 'Save')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {telecomunication.telegram_all_integrated[langIndex]}
          </Typography>
        )}
      </Box>

      <Box>
        {created.length > 0 ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, pb: 1.5, borderBottom: '1px solid #e5e8ed' }}>
              {telecomunication.telegram_already_created[langIndex]}
            </Typography>
            <div className="short-cards">
            {created.map((item, index) => (
              <Paper key={index} className="short-card" elevation={0}>
                <div className="short-card__label">
                  {item.buddy} {item.notes && `(${item.notes})`}
                </div>
                <div className="short-card__code">
                  {item.telegram && (
                    <Box component="span" sx={{ display: 'block', mb: 0.5 }}>
                      {telecomunication.telegram_label[langIndex]} {item.telegram.phone_number}
                    </Box>
                  )}
                  {item.redirects && item.redirects.length > 0 && (
                    <Box component="span" sx={{ display: 'block' }}>
                    </Box>
                  )}
                </div>
                <div className="short-card__actions">
                  <span />
                  <span
                    onClick={() => handleDelete(item.buddy)}
                    className="delete"
                    style={{ color: '#ce405f', cursor: 'pointer', margin: 0, fontSize: 16 }}
                    title={telecomunication.telegram_delete_confirm?.[langIndex] || 'Delete'}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </span>
                </div>
              </Paper>
            ))}
            </div>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {telecomunication.telegram_no_integrations[langIndex]}
          </Typography>
        )}
      </Box>
    </Box>
  )
}